/**
 * Customer Service Ticket System Interfaces
 */

// Ticket Status Types
export type TicketStatus = 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed';

// Ticket Priority Types
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

// Ticket Category Types
export type TicketCategory = 'support' | 'sales' | 'billing' | 'feedback' | 'feature_request';

/**
 * Main Ticket Interface
 */
export interface ITicket {
    ticket_id: number;
    customer_email: string;
    customer_name: string;
    business_id: number | null;
    business_name: string | null;
    subject: string;
    description: string;
    category: TicketCategory;
    status: TicketStatus;
    priority: TicketPriority;
    assigned_to: string | null;
    user_email: string | null;
    enterprise: boolean;
    dashboard: boolean;
    ip_address: string | null;
    user_agent: string | null;
    created_at: string;
    updated_at: string;
    closed_at: string | null;
    first_response_at: string | null;
    first_response_by: string | null;
    resolution_notes: string | null;
    has_unread_customer_message?: boolean;
    last_customer_reply_at?: string | null;
    last_support_reply_at?: string | null;
    messages?: ITicketMessage[];
}

/**
 * Ticket Message Interface
 */
export interface ITicketMessage {
    message_id: number;
    ticket_id: number;
    message: string;
    is_internal: boolean;
    is_customer: boolean;
    sender_name: string;
    sender_email: string;
    email_sent: boolean;
    email_sent_at: string | null;
    created_at: string;
}

/**
 * Create Ticket Request
 */
export interface ICreateTicketRequest {
    email: string;
    name: string;
    subject: string;
    description: string;
    category?: TicketCategory;
    business_id?: number | null;
    user_email?: string | null;
    business_name?: string | null;
    ip_address?: string | null;
    user_agent?: string | null;
    enterprise?: boolean;
    dashboard?: boolean;
}

/**
 * Create Ticket Response
 */
export interface ICreateTicketResponse {
    success: boolean;
    ticket_id?: number;
    message?: string;
    error?: string;
}

/**
 * Get Tickets Filter Request
 */
export interface IGetTicketsFilter {
    status?: TicketStatus;
    exclude_status?: TicketStatus;
    priority?: TicketPriority;
    category?: TicketCategory;
    assigned_to?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
    unanswered_only?: boolean;
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
}

/**
 * Get Tickets Response
 */
export interface IGetTicketsResponse {
    success: boolean;
    tickets?: ITicket[];
    total?: number;
    page?: number;
    per_page?: number;
    total_pages?: number;
    error?: string;
}

/**
 * Get Single Ticket Response
 */
export interface IGetTicketResponse {
    success: boolean;
    ticket?: ITicket;
    error?: string;
}

/**
 * Update Ticket Request
 */
export interface IUpdateTicketRequest {
    ticket_id: number;
    status?: TicketStatus;
    priority?: TicketPriority;
    assigned_to?: string | null;
}

/**
 * Update Ticket Response
 */
export interface IUpdateTicketResponse {
    success: boolean;
    ticket?: Partial<ITicket>;
    error?: string;
}

/**
 * Add Ticket Message Request
 */
export interface IAddTicketMessageRequest {
    ticket_id: number;
    message: string;
    is_internal?: boolean;
    send_email?: boolean;
    sender_name?: string;
    sender_email?: string;
    cc_email?: string;
}

/**
 * Add Ticket Message Response
 */
export interface IAddTicketMessageResponse {
    success: boolean;
    message?: ITicketMessage;
    error?: string;
}

/**
 * Close Ticket Request
 */
export interface ICloseTicketRequest {
    ticket_id: number;
    resolution_notes?: string;
}

/**
 * Close Ticket Response
 */
export interface ICloseTicketResponse {
    success: boolean;
    ticket?: Partial<ITicket>;
    error?: string;
}

/**
 * Ticket Statistics
 */
export interface ITicketStats {
    total: number;
    open: number;
    in_progress: number;
    waiting_customer: number;
    resolved: number;
    closed: number;
    by_priority: {
        low: number;
        medium: number;
        high: number;
        urgent: number;
    };
    by_category: {
        support: number;
        sales: number;
        billing: number;
        feedback: number;
        feature_request: number;
    };
    today: number;
    this_week: number;
    this_month: number;
    avg_response_time_hours: string | null;
}

/**
 * Get Ticket Stats Response
 */
export interface IGetTicketStatsResponse {
    success: boolean;
    stats?: ITicketStats;
    error?: string;
}

/**
 * Ticket Status Options for UI
 */
export const TICKET_STATUS_OPTIONS: { value: TicketStatus; label: string; color: string }[] = [
    { value: 'open', label: 'Open', color: '#4CAF50' },
    { value: 'in_progress', label: 'In Progress', color: '#2196F3' },
    { value: 'waiting_customer', label: 'Waiting on Customer', color: '#FF9800' },
    { value: 'resolved', label: 'Resolved', color: '#9C27B0' },
    { value: 'closed', label: 'Closed', color: '#607D8B' }
];

/**
 * Ticket Priority Options for UI
 */
export const TICKET_PRIORITY_OPTIONS: { value: TicketPriority; label: string; color: string }[] = [
    { value: 'low', label: 'Low', color: '#607D8B' },
    { value: 'medium', label: 'Medium', color: '#2196F3' },
    { value: 'high', label: 'High', color: '#FF9800' },
    { value: 'urgent', label: 'Urgent', color: '#F44336' }
];

/**
 * Ticket Category Options for UI
 */
export const TICKET_CATEGORY_OPTIONS: { value: TicketCategory; label: string; icon: string }[] = [
    { value: 'support', label: 'Technical Support', icon: 'support_agent' },
    { value: 'sales', label: 'Sales', icon: 'shopping_cart' },
    { value: 'billing', label: 'Billing', icon: 'receipt' },
    { value: 'feedback', label: 'Feedback', icon: 'feedback' },
    { value: 'feature_request', label: 'Feature Request', icon: 'lightbulb' }
];

/**
 * Customer Ticket (limited view for customers)
 */
export interface ICustomerTicket {
    ticket_id: number;
    customer_email: string;
    customer_name: string;
    subject: string;
    description: string;
    category: TicketCategory;
    status: TicketStatus;
    priority: TicketPriority;
    created_at: string;
    updated_at: string;
    closed_at: string | null;
    messages?: ICustomerTicketMessage[];
}

/**
 * Customer Ticket Message (limited view - no internal notes)
 */
export interface ICustomerTicketMessage {
    message_id: number;
    message: string;
    is_customer: boolean;
    sender_name: string;
    created_at: string;
}

/**
 * Get Customer Ticket Request
 */
export interface IGetCustomerTicketRequest {
    ticket_hash: string;
}

/**
 * Get Customer Ticket Response
 */
export interface IGetCustomerTicketResponse {
    success: boolean;
    ticket?: ICustomerTicket;
    error?: string;
}

/**
 * Add Customer Ticket Message Request
 */
export interface IAddCustomerTicketMessageRequest {
    ticket_hash: string;
    message: string;
}

/**
 * Add Customer Ticket Message Response
 */
export interface IAddCustomerTicketMessageResponse {
    success: boolean;
    message?: ICustomerTicketMessage;
    error?: string;
}

/**
 * Canned Response Interface
 */
export interface ICannedResponse {
    response_id: number;
    title: string;
    message: string;
    category: string;
    created_by: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * Get Canned Responses Response
 */
export interface IGetCannedResponsesResponse {
    success: boolean;
    responses?: ICannedResponse[];
    error?: string;
}

/**
 * Add Canned Response Request
 */
export interface IAddCannedResponseRequest {
    title: string;
    message: string;
    category?: string;
    created_by?: string;
}

/**
 * Add Canned Response Response
 */
export interface IAddCannedResponseResponse {
    success: boolean;
    response?: ICannedResponse;
    error?: string;
}

/**
 * Update Canned Response Request
 */
export interface IUpdateCannedResponseRequest {
    response_id: number;
    title?: string;
    message?: string;
    category?: string;
}

/**
 * Update Canned Response Response
 */
export interface IUpdateCannedResponseResponse {
    success: boolean;
    response?: ICannedResponse;
    error?: string;
}

/**
 * Delete Canned Response Request
 */
export interface IDeleteCannedResponseRequest {
    response_id: number;
}

/**
 * Delete Canned Response Response
 */
export interface IDeleteCannedResponseResponse {
    success: boolean;
    error?: string;
}

/**
 * Canned Response Category Options for UI
 */
export const CANNED_RESPONSE_CATEGORY_OPTIONS: { value: string; label: string }[] = [
    { value: 'general', label: 'General' },
    { value: 'greeting', label: 'Greeting' },
    { value: 'technical', label: 'Technical' },
    { value: 'billing', label: 'Billing' },
    { value: 'sales', label: 'Sales' },
    { value: 'closing', label: 'Closing' }
];
